import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x3_ueberspannungsschutz';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface UbschutzInfo {
  name: string;
  titel: string;
  allgemeineBeschreibung: string;
  typKlassifizierung: string;
  anwendungsbeispiele: string;
  auslegungsHinweise: string;
  gesetzeNormenRelevanz: string;
  anschlussPrinzip: string;
  wichtigeParameter: string;
  herstellerReferenz?: string;
}

const ubschutzTypen: UbschutzInfo[] = [
  {
    name: 'DEHNshield Typ 1+2 (Vorzähler)',
    titel: 'DEHNshield – Kombiableiter Typ 1+2',
    allgemeineBeschreibung: 'Kombi-Ableiter für Blitzstrom- und Überspannungsschutz (Typ 1+2) im Vorzählerbereich. Geeignet für TN- und TT-Systeme als Grob- und Mittelschutz.',
    typKlassifizierung: 'Typ 1+2 Kombiableiter (Blitzstrom-Ableiter + Überspannungsableiter)',
    anwendungsbeispiele: 'Hauptverteilungen in Gebäuden mit äußerem Blitzschutz, Industrieanlagen mit hohem Ableitbedarf, Einspeisepunkte am Gebäudeeintritt.',
    auslegungsHinweise: 'Leitungsführung kurz und gerade halten (≤ 0,5 m), geeignete Vorsicherung gemäß Herstellerdaten, Leiterquerschnitte und Netzform (TN/TT/IT) beachten.',
    gesetzeNormenRelevanz: 'DIN VDE 0100-534 (Auswahl und Errichtung von Überspannungsschutzeinrichtungen), DIN VDE 0100-443 (Schutz bei transienten Überspannungen).',
    anschlussPrinzip: 'Paralleler Anschluss an die Phasenleiter und den Neutralleiter, Erdung über PE. Bei TT-Systemen N-PE-Funkenstrecke beachten.',
    wichtigeParameter: 'Iimp (10/350 µs Blitzstromstoß), In/Imax (8/20 µs), Up (Schutzpegel), Netzform, Vorsicherung, Ableitweg.',
    herstellerReferenz: 'DEHNshield ZP – Datenblatt laut Hersteller'
  },
  {
    name: 'DEHNguard Typ 2 (Unterverteilung)',
    titel: 'DEHNguard – Überspannungsableiter Typ 2',
    allgemeineBeschreibung: 'Feinschutz / Mittelschutz in Unterverteilungen zur Begrenzung verbleibender Überspannungen.',
    typKlassifizierung: 'Typ 2 Überspannungsableiter (SPD Typ 2)',
    anwendungsbeispiele: 'Unterverteilungen in Büro- und Wohngebäuden, Maschinenverteilungen nach Typ 1 an Einspeisung.',
    auslegungsHinweise: 'Kurze Anschlussleitungen, Selektivität und Koordination mit Typ 1 Ableitern, Austausch von Ableitmodulen bei Auslösung.',
    gesetzeNormenRelevanz: 'DIN VDE 0100-534, Herstellervorgaben zur Koordination mit Typ 1.',
    anschlussPrinzip: 'Paralleler Anschluss an L/N/PE, ggf. Remote-Signalisierung über potentialfreie Kontakte.',
    wichtigeParameter: 'In, Imax, Up, Nennspannung, Netzform.',
    herstellerReferenz: 'DEHNguard Modular – Datenblatt'
  },
  {
    name: 'DEHNrail/BLITZDUCTOR (Signalleitungen)',
    titel: 'DEHNrail / BLITZDUCTOR – Schutz für MSR/Signalleitungen',
    allgemeineBeschreibung: 'Ableiter für Mess-, Steuer- und Regeltechnik, Bussysteme und Telekommunikationsleitungen.',
    typKlassifizierung: 'Schutz für Daten-/Signalleitungen (z. B. RS485, 0–10 V, 4–20 mA).',
    anwendungsbeispiele: 'SPS-E/A-Module, Sensorleitungen, Busschnittstellen.',
    auslegungsHinweise: 'Anzahl der Adern/Paare beachten, Spannungspegel des Signals, Ableiter nahe am zu schützenden Gerät platzieren.',
    gesetzeNormenRelevanz: 'DIN EN 61643-21, EMV-gerechte Installation.',
    anschlussPrinzip: 'Reihenklemm-ähnlicher Durchschleifanschluss mit Erdbezug, definierte Ein- und Ausgangsrichtung einhalten.',
    wichtigeParameter: 'Nennspannung des Signals, Schutzpegel Up, Leitungsart (symmetrisch/unsymmetrisch), Dämpfung.',
    herstellerReferenz: 'BLITZDUCTORconnect – Datenblatt'
  }
];

const introBlocks = [
  {
    heading: 'Überspannungsschutz – Zweck und Ebenen',
    body: 'Überspannungsschutzeinrichtungen (SPD) schützen Anlagen vor transienten Überspannungen durch Blitz-/Schaltvorgänge. Konzeptionell erfolgt der Schutz in Stufen: Typ 1 (Grob, Blitzstrom), Typ 2 (Mittel, Unterverteilung), Typ 3 (Feinschutz an Endgeräten) sowie separate Schutzgeräte für Signalleitungen.'
  },
  {
    heading: 'Planung und Normen',
    body: 'Planung nach DIN VDE 0100-443/534. Wichtige Prinzipien: kurze Anschlusswege, Koordination der Stufen, Netzform beachten (TN/TT/IT), richtige Vorsicherung und Querschnitte.'
  }
];

const M6_U7_X3_Ueberspannungsschutz: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedType, setSelectedType] = React.useState<string>(ubschutzTypen[0].name);
  const currentInfo = ubschutzTypen.find(u => u.name === selectedType) || ubschutzTypen[0];

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({
      ...tb,
      body: override?.texts?.[`intro_${i}`] || tb.body
    })),
    [override]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    { heading: currentInfo.titel, body: override?.texts?.[`dyn_${selectedType}_0`] || currentInfo.allgemeineBeschreibung },
    { heading: 'Typ/Klassifizierung', body: override?.texts?.[`dyn_${selectedType}_1`] || currentInfo.typKlassifizierung },
    { heading: 'Anwendungsbeispiele', body: override?.texts?.[`dyn_${selectedType}_2`] || currentInfo.anwendungsbeispiele },
    { heading: 'Hinweise zur Auslegung', body: override?.texts?.[`dyn_${selectedType}_3`] || currentInfo.auslegungsHinweise },
    { heading: 'Gesetze und Normen', body: override?.texts?.[`dyn_${selectedType}_4`] || currentInfo.gesetzeNormenRelevanz },
    { heading: 'Anschlussprinzip', body: override?.texts?.[`dyn_${selectedType}_5`] || currentInfo.anschlussPrinzip },
    { heading: 'Wichtige Parameter', body: override?.texts?.[`dyn_${selectedType}_6`] || currentInfo.wichtigeParameter }
  ], [selectedType, override, currentInfo]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() =>
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Bild-Upload:', error);
      alert('Fehler beim Hochladen des Bildes.');
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Text-Speichern:', error);
      alert('Fehler beim Speichern des Textes.');
    }
  }, [token, refresh]);

  const handleTypeChange = (event: any) => {
    setSelectedType(String(event.target.value));
  };

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.7.3 Überspannungsschutz', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'Überspannungsschutz',
            label: 'SPD-Bild',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, {
          key: 0,
          pageId: PAGE_ID,
          blockId: 'intro_0',
          heading: mergedIntroBlocks[0].heading,
          body: mergedIntroBlocks[0].body,
          onSave: (newBody: string) => handleTextSave('intro_0', newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: 1,
          pageId: PAGE_ID,
          blockId: 'intro_1',
          heading: mergedIntroBlocks[1].heading,
          body: mergedIntroBlocks[1].body,
          onSave: (newBody: string) => handleTextSave('intro_1', newBody)
        }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'ubschutz-select-label' }, 'Schutztyp auswählen'),
          React.createElement(Select, {
            labelId: 'ubschutz-select-label',
            id: 'ubschutz-select',
            value: selectedType,
            label: 'Schutztyp auswählen',
            onChange: handleTypeChange
          },
            ...ubschutzTypen.map((typ) =>
              React.createElement(MenuItem, { key: typ.name, value: typ.name }, typ.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedType, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedType}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_0`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_0`,
          heading: mergedDynamicBlocks[0]?.heading || 'Allgemeine Beschreibung',
          body: mergedDynamicBlocks[0]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_0`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_1`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_1`,
          heading: 'Typ/Klassifizierung',
          body: mergedDynamicBlocks[1]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_1`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_2`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_2`,
          heading: 'Anwendungsbeispiele',
          body: mergedDynamicBlocks[2]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_2`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_3`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_3`,
          heading: 'Hinweise zur Auslegung',
          body: mergedDynamicBlocks[3]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_3`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_4`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_4`,
          heading: 'Gesetze und Normen',
          body: mergedDynamicBlocks[4]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_4`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_5`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_5`,
          heading: 'Anschlussprinzip',
          body: mergedDynamicBlocks[5]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_5`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_6`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_6`,
          heading: 'Wichtige Parameter',
          body: mergedDynamicBlocks[6]?.body || '',
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_6`, newBody)
        }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u7_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M6_U7_X3_Ueberspannungsschutz;


